import React, { Fragment, SFC } from 'react';
import { ThemeProps, withTheme } from '../styled-components';
import Meta from './atoms/Meta';
import CollectionIntro from './organisms/collections/CollectionIntro';
import gql from 'graphql-tag';
import { RouteComponentProps, withRouter } from 'react-router';
import { DataProps, graphql } from 'react-apollo';
import { compose, withState } from 'recompose';
import Container from './atoms/Container';
import { ExchangeParams } from './Exchange';
import {
    AllCollectionsQuery as Response,
    AllCollectionsQueryVariables,
    EventCollectionItemFragment,
    EventCollectionPageFragment,
    FeaturedStoryCollectionItemFragment,
    NewsCollectionItemFragment,
    NewsCollectionPageFragment,
    StoriesCollectionPageFragment,
    StoryCollectionItemFragment,
    ServiceCollectionItemFragment,
    DocumentationCollectionItemFragment,
} from '../entities/operationResults';
import { fragments as CollectionFragments } from './organisms/collections/CollectionItem';
import CollectionFeaturedItems, { fragments as CollectionFeaturedFragments } from './organisms/collections/CollectionFeaturedItems';
import { getEntriesOfType, getEntryOfType } from '../services/entryTypes';
import CollectionList from './organisms/collections/CollectionList';
import ContentBody from './atoms/ContentBody';
import { eventsRoute, newsRoute } from '../constants/routes';
import CollectionPagination from './organisms/collections/CollectionPagination';
import IntroTitle from './atoms/IntroTitle';
import withSpinner from '../hocs/withSpinner';
import { path } from 'ramda';
import queryString from 'query-string';
import { startOfDay } from 'date-fns';

const GET_COLLECTIONS_QUERY = gql`
    query AllCollections(
      $site: [String!],
      $type: [String],
      $collectionType: [String],
      $offset: Int!,
      $itemLimit: Int!,
      $order: String,
      $listExpiryDate: [QueryArgument],
    ) {
      collectionItems: entries(
        site: $site,
        offset: $offset,
        limit: $itemLimit,
        section: $type,
        orderBy: $order,
        listExpiryDate: $listExpiryDate,
      ) {
        ... on event_Entry {
          ...EventCollectionItem
        }
        ... on news_Entry {
          ...NewsCollectionItem
        }
        ... on story_Entry {
          ...StoryCollectionItem
        }
        ... on info_Entry {
          ...InfoCollectionItem
        }
        ... on documentation_Entry {
          ...DocumentationCollectionItem
        }
        ... on service_Entry {
          ...ServiceCollectionItem
        }
      }
      entryCount(section: $type, listExpiryDate: $listExpiryDate)
      collection: entries(site: $site, section: $collectionType) {
        ... on eventCollection_Entry {
          ...EventCollectionPage
        }
        ... on newsCollection_Entry {
          ...NewsCollectionPage
        }
        ... on storyCollection_Entry {
          ...StoriesCollectionPage
        }
      }
    }
    
    fragment EventCollectionPage on eventCollection_Entry {
      introHeading
      introText {
        html
      }
    }

    fragment NewsCollectionPage on newsCollection_Entry {
      introHeading
      introText {
        html
      }
    }

    fragment StoriesCollectionPage on storyCollection_Entry {
      introHeading
      introText {
        html
      }
      featuredStories {
        ...FeaturedStoryCollectionItem
      }
    }
    ${CollectionFragments.event}
    ${CollectionFragments.news}
    ${CollectionFragments.story}
    ${CollectionFeaturedFragments.relatedStory}
    ${CollectionFragments.documentation}
    ${CollectionFragments.service}
    ${CollectionFragments.info}
`;

type Props = DataProps<Response, AllCollectionsQueryVariables> & ThemeProps & RouteComponentProps<ExchangeParams>;

const enhance = compose(
    withRouter,
    withTheme,
    withState('currentPage', 'setCurrentPage', 1),
    graphql(GET_COLLECTIONS_QUERY, {
        options: (({ match, location: { search } }: Props) => {
            const limit: number = 8;
            let offset: number = 0;
            let collectionType: string;
            let collectionPageType: string;
            let order: string | null = null;
            let listExpiryDate: string | null = null;
            const currentPage = parseInt(queryString.parse(search).page, 10) || 1;

            switch (match.path) {
                case eventsRoute.path:
                    collectionType = "event";
                    collectionPageType = "eventCollection";
                    order = 'startDate ASC';
                    listExpiryDate = `>= ${Math.round((+startOfDay(Date.now())) / 1000)}`;
                    break;
                case newsRoute.path:
                    collectionType = "news";
                    collectionPageType = "newsCollection";
                    order = 'postDate DESC';
                    break;
                default:
                    collectionType = "story";
                    collectionPageType = "storyCollection";
                    order = 'postDate DESC';
                    break;
            }
            if (currentPage !== 1) {
                offset = (currentPage - 1) * limit;
            }

            return {
                variables: {
                    site: match.params.exchange,
                    type: collectionType,
                    collectionType: collectionPageType,
                    offset,
                    itemLimit: limit,
                    order,
                    listExpiryDate
                }
            };
        })
    }),
    withSpinner
);

const Collection: SFC<Props> = ({ data, theme, match }) => {
    let collectionItems: Array<NewsCollectionItemFragment | StoryCollectionItemFragment | EventCollectionItemFragment | ServiceCollectionItemFragment | DocumentationCollectionItemFragment>;
    let collectionPage: StoriesCollectionPageFragment | EventCollectionPageFragment | NewsCollectionPageFragment | null;

    if (match.path === eventsRoute.path) {
        collectionItems = getEntriesOfType('event_Entry', data.collectionItems);
        collectionPage = getEntryOfType('eventCollection_Entry', data.collection && data.collection[0]);
    } else if (match.path === newsRoute.path) {
        collectionItems = getEntriesOfType('news_Entry', data.collectionItems);
        collectionPage = getEntryOfType('newsCollection_Entry', data.collection && data.collection[0]);
    } else {
        collectionItems = getEntriesOfType('story_Entry', data.collectionItems);
        collectionPage = getEntryOfType('storyCollection_Entry', data.collection && data.collection[0]);
    }

    const entryCount = path<number>(['entryCount'], data || {});
    const totalPages = entryCount ? Math.ceil(entryCount / 8) : 1;
    const title = path<string>(['introHeading'], collectionPage || {});
    const intro = path<string>(['introText', 'html'], collectionPage || {});

    const featuredItems: FeaturedStoryCollectionItemFragment[] | null = collectionPage && collectionPage.__typename === 'storyCollection_Entry' ? getEntriesOfType('story_Entry', collectionPage.featuredStories) : null;

    return (
        <Fragment>
            <Meta title={title} />
            <CollectionIntro
                featuredEntries={featuredItems !== null ?
                    <CollectionFeaturedItems collectionItems={featuredItems} /> : null}
            >
                <div>
                    <IntroTitle
                        color={theme.colors.white}
                    >
                        {title}
                    </IntroTitle>
                    {intro && <ContentBody color={theme.colors.white} dangerouslySetInnerHTML={{ __html: intro }} />}
                </div>
            </CollectionIntro>
            <Container
                maxWidth
                paddingTop={'5.6rem'}
                paddingTopL={'5.6rem'}
                paddingBottom={'4rem'}
                paddingBottomL={'4rem'}
                backgroundColor={theme.colors.white}
            >
              <CollectionList collectionItems={collectionItems}/>

            {totalPages && totalPages > 1 && (
                <CollectionPagination
                    totalPages={totalPages}
                />
            )}
            </Container>
        </Fragment>
    );
};

export default enhance(Collection);
